import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    InputLabel,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Timestamp } from 'firebase/firestore';
import ConfirmModal, { ConfirmModalType } from '../common/ConfirmModal';
import OrgPropertiesService from '../../services/org-properties.service';
import { Loader } from '../index';
import { propertyTypes, userTypes as UserRole } from '../../utils/constants';
import SnackbarNotify from '../../utils/snackbar/SnackbarNotify';
import RentalService from '../../services/rental.service';
import { capitalizeFirstLetterMulti } from '../../utils/Helpers';
import UpdateUserModal from 'sections/view-edit-org/UpdateUserModal';
import { openModal } from 'utils/modal/modal.helper';
import { MODAL_ACTION_TYPE } from 'utils/constants/modal';
import OrgService from 'services/org.service';

const RENTAL_MANAGER_ROLE = [
    {
        key: UserRole.RENTAL_MANAGER,
        name: 'Rental Manager'
    }
];

const SetUpdateRentalModal = ({
    onClose,
    onUpdate,
    onTerminate,
    property,
    activeOrg
}) => {
    const [openConfirmRent, setOpenConfirmRent] = useState(false);
    const [openConfirmTerminate, setOpenConfirmTerminate] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('long');
    const [rentalManagers, setRentalManagers] = useState([]);
    const [primaryManagers, setPrimaryManagers] = useState([]);
    const [selectedManagers, setSelectedManagers] = useState([]);
    const [ownerManaged, setOwnerManaged] = useState(false);
    const hasShortTermRental = activeOrg.modules?.includes('short_term_rental');
    const [addingManager, setAddingManager] = useState(false);
    const [managersLoading, setManagersLoading] = useState(false);

    const toTimestampOrNull = v => (v ? Timestamp.fromDate(new Date(v)) : null);
    const tsToDate = v => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

    const validationSchema = Yup.object().shape({
        rental_start: Yup.mixed()
            .typeError('Enter a valid start date.')
            .required('Start date is required.'),
        rental_end: Yup.mixed()
            .typeError('Enter a valid end date.')
            .required('End date is required.')
            .test(
                'is-after',
                'The end date must be after the start date.',
                function (value) {
                    const { rental_start } = this.parent;
                    if (!value || !rental_start) return true;
                    return new Date(value) > new Date(rental_start);
                }
            )
    });

    useEffect(() => {
        if (property?.owner_managed) {
            setOwnerManaged(true);
        }
    }, [property]);

    const handleSubmitWithConfirm = formValues => {
        setPendingValues(formValues);
        if (!(property?.type === propertyTypes.RENTAL)) {
            setOpenConfirmRent(true);
        } else {
            confirmRent(formValues);
        }
    };

    const confirmRent = async (formValues = pendingValues) => {
        if (!formValues) return;
        setLoading(true);

        try {
            const rentalPayload = {
                type: propertyTypes.RENTAL,
                rental_start: toTimestampOrNull(formValues.rental_start),
                rental_end: toTimestampOrNull(formValues.rental_end)
            };

            await OrgPropertiesService.setRentalProperty(
                property.org_id,
                property.id,
                rentalPayload
            );

            SnackbarNotify.success('Property has been rented successfully.');
            onUpdate?.(rentalPayload);
            onClose();
        } catch (err) {
            SnackbarNotify.error('Failed to rent property. Please try again.');
            console.error('Error setting rental:', err);
        } finally {
            setLoading(false);
            setPendingValues(null);
            setOpenConfirmRent(false);
        }
    };

    const confirmTerminate = async () => {
        setLoading(true);

        try {
            await OrgPropertiesService.resetRentalProperty(property.org_id, property.id, {
                type: propertyTypes.RESIDENTIAL,
                rental_start: null,
                rental_end: null,
                owner_managed: false
            });

            await RentalService.syncOwnerManagedForProperty(
                property.org_id,
                property.id,
                { owner_managed: false }
            );

            SnackbarNotify.success('Rental has been terminated successfully.');
            onTerminate?.();
            onClose();
        } catch (err) {
            SnackbarNotify.error('Failed to terminate rental. Please try again.');
            console.error('Error terminating rental:', err);
        } finally {
            setLoading(false);
            setOpenConfirmTerminate(false);
        }
    };

    useEffect(() => {
        const fetchRentalManagers = async () => {
            setManagersLoading(true);
            try {
                const [primaryManagers, allManagers] = await Promise.all([
                    RentalService.getOrgPrimaryRentalManagers(property.org_id),
                    RentalService.getOrgRentalManagers(property.org_id)
                ]);
                setRentalManagers(allManagers);
                setPrimaryManagers(primaryManagers);

                if (!property || allManagers.length === 0) return;

                const existingManagers = allManagers.filter(manager =>
                    manager.managed_property_ids?.includes(property.id)
                );

                setSelectedManagers(existingManagers);
            } catch (err) {
                console.error('Failed to fetch rental managers', err);
            } finally {
                setManagersLoading(false);
            }
        };
        fetchRentalManagers();
    }, [property.org_id]);

    const isRental = property?.type === propertyTypes.RENTAL;

    const handleSaveShortTermManagers = async managers => {
        if (!ownerManaged && primaryManagers.length === 0 && managers.length === 0) {
            SnackbarNotify.error('Please select at least one rental manager.');
            return;
        }
        setLoading(true);
        const managerIds = managers
            .filter(m => m.status !== 'invited')
            .map(m => m.id);
        const inviteIds = managers
            .filter(m => m.status === 'invited')
            .map(m => m.invite_id);
        try {
            const rentalPayload = {
                type: propertyTypes.RENTAL,
                rental_start: null,
                rental_end: null,
                owner_managed: ownerManaged
            };

            await OrgPropertiesService.setRentalProperty(
                property.org_id,
                property.id,
                rentalPayload
            );

            await RentalService.syncPropertyManagers(
                property.org_id,
                property.id,
                managerIds
            );

            await RentalService.syncInvitedManagers(
                property.org_id,
                property.id,
                inviteIds
            );

            await RentalService.syncOwnerManagedForProperty(
                property.org_id,
                property.id,
                { owner_managed: ownerManaged }
            );

            SnackbarNotify.success('Property has been rented successfully.');
            onUpdate?.(rentalPayload);
            onClose();
        } catch (err) {
            SnackbarNotify.error('Failed to rent property. Please try again.');
            console.error('Error setting rental:', err);
        } finally {
            setLoading(false);
        }
    };

    const isShortTermRental =
        property?.type === propertyTypes.RENTAL &&
        !property?.rental_start &&
        !property?.rental_end;

    const isLongTermRental =
        property?.type === propertyTypes.RENTAL &&
        (property?.rental_start || property?.rental_end);

    useEffect(() => {
        if (property?.type === propertyTypes.RENTAL) {
            if (property?.rental_start || property?.rental_end) {
                setTab('long');
            } else {
                setTab('short');
            }
        }
    }, [property]);

    const handleOpenAddManager = () => {
        const handleCreateUser = async newUser => {
            setAddingManager(true);
            try {
                await OrgService.addOrgManager(property.org_id, {
                    ...newUser,
                    managed_property_ids: [property.id]
                });

                SnackbarNotify.success(
                    `Manager ${newUser.first_name} ${newUser.last_name} added.`
                );

                const [primaryManagers, allManagers] = await Promise.all([
                    RentalService.getOrgPrimaryRentalManagers(property.org_id),
                    RentalService.getOrgRentalManagers(property.org_id)
                ]);

                setRentalManagers(allManagers);
                setPrimaryManagers(primaryManagers);
                const newManager = allManagers.find(m => m.email === newUser.email);

                if (!newManager) {
                    return;
                }
                setSelectedManagers(prev => [...prev, newManager]);
            } catch (error) {
                SnackbarNotify.error(`Error: ${error?.message || 'Unexpected error'}`);
            } finally {
                setAddingManager(false);
            }
        };

        openModal(
            UpdateUserModal,
            {
                type: MODAL_ACTION_TYPE.ADD,
                onCreateOrUpdate: handleCreateUser,
                roles: RENTAL_MANAGER_ROLE
            },
            { fullWidth: true }
        );
    };

    return (
        <>
            {loading && <Loader />}
            <DialogTitle>{isRental ? 'Edit Rental Property' : 'Rent Property'}</DialogTitle>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="fullWidth"
                sx={{ px: 2 }}
            >
                {hasShortTermRental && (
                    <Tab label="Short-Term" value="short" disabled={isLongTermRental} />
                )}

                <Tab label="Long-Term" value="long" disabled={isShortTermRental} />
            </Tabs>
            <Divider />
            {tab === 'short' && (
                <>
                    <DialogContent sx={{ p: 3, width: '500px' }}>
                        {managersLoading || addingManager ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    my: 1,
                                    paddingTop: '100px',
                                    paddingBottom: '100px'
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                {primaryManagers?.length > 0 && (
                                    <Grid
                                        container
                                        sx={{
                                            mb: 2,
                                            border: '1px solid #ccc',
                                            p: 2,
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                These managers can manage all rentals on
                                                this property:
                                            </Typography>
                                        </Grid>

                                        {primaryManagers.map(manager => (
                                            <Grid item xs={12} key={manager.id}>
                                                {capitalizeFirstLetterMulti(
                                                    manager.first_name
                                                )}{' '}
                                                {capitalizeFirstLetterMulti(
                                                    manager.last_name
                                                )}{' '}
                                                ({manager.email})
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Stack spacing={0.5}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={ownerManaged}
                                                    onChange={e =>
                                                        setOwnerManaged(e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Owner managed"
                                        />
                                    </Stack>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="rental_managers">
                                            Rental Managers
                                        </InputLabel>
                                        <Box>
                                            <Autocomplete
                                                multiple
                                                id="rental_managers"
                                                options={[
                                                    ...(rentalManagers || []),
                                                    { id: 'add-new', isAddNew: true }
                                                ]}
                                                getOptionLabel={option => {
                                                    if (option.isAddNew)
                                                        return '+ Add new manager';

                                                    const label = `${capitalizeFirstLetterMulti(
                                                        option.first_name
                                                    )} ${capitalizeFirstLetterMulti(
                                                        option.last_name
                                                    )}${option.company_name ? ` · ${option.company_name}` : ''} (${option.email})`;

                                                    return option.status === 'invited'
                                                        ? `${label} (Invited)`
                                                        : label;
                                                }}
                                                value={selectedManagers}
                                                isOptionEqualToValue={(option, value) =>
                                                    option.id === value.id
                                                }
                                                onChange={(_, newValue) => {
                                                    const last =
                                                        newValue[newValue.length - 1];

                                                    if (last?.isAddNew) {
                                                        handleOpenAddManager();
                                                        return;
                                                    }

                                                    setSelectedManagers(
                                                        newValue.filter(v => !v.isAddNew)
                                                    );
                                                }}
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select managers"
                                                        fullWidth
                                                        helperText={
                                                            ownerManaged
                                                                ? 'Managers are optional when owner managed is enabled.'
                                                                : primaryManagers?.length ===
                                                                  0
                                                                ? 'You must select at least one rental manager.'
                                                                : 'Optional: select additional rental managers.'
                                                        }
                                                    />
                                                )}
                                                disabled={loading}
                                                componentsProps={{
                                                    popper: {
                                                        modifiers: [
                                                            { name: 'flip', enabled: true },
                                                            {
                                                                name: 'preventOverflow',
                                                                enabled: true,
                                                                options: { boundary: 'window' }
                                                            }
                                                        ]
                                                    }
                                                }}
                                                ListboxProps={{
                                                    sx: {
                                                        maxHeight: 300,
                                                        overflow: 'auto',
                                                        '& .MuiAutocomplete-option': {
                                                            textTransform: 'none'
                                                        }
                                                    }
                                                }}
                                            />{' '}
                                        </Box>
                                        {loading &&
                                            primaryManagers?.length === 0 &&
                                            selectedManagers.length === 0 &&
                                            ownerManaged === false && (
                                                <Typography
                                                    variant="caption"
                                                    color="error"
                                                >
                                                    At least one manager must be selected
                                                </Typography>
                                            )}
                                    </Stack>
                                </Grid>
                            </>
                        )}
                    </DialogContent>

                    <Divider />

                    <DialogActions sx={{ p: 2.5 }}>
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid item>
                                <Button
                                    color="error"
                                    variant="text"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                            </Grid>

                            <Grid item>
                                <Grid container spacing={2}>
                                    {isRental && (
                                        <Grid item>
                                            <Button
                                                color="error"
                                                variant="outlined"
                                                onClick={() =>
                                                    setOpenConfirmTerminate(true)
                                                }
                                                disabled={loading}
                                            >
                                                Terminate Rental
                                            </Button>
                                        </Grid>
                                    )}

                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            onClick={() =>
                                                handleSaveShortTermManagers(
                                                    selectedManagers
                                                )
                                            }
                                            disabled={loading}
                                        >
                                            {isRental ? 'Update' : 'Save'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </>
            )}
            {tab === 'long' && (
                <Formik
                    initialValues={{
                        rental_start: tsToDate(property?.rental_start) || null,
                        rental_end: tsToDate(property?.rental_end) || null
                    }}
                    onSubmit={handleSubmitWithConfirm}
                    validationSchema={validationSchema}
                    validateOnChange
                >
                    {({ handleSubmit, setFieldValue, touched, errors, values }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DialogContent sx={{ p: 2.5 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label={isRental ? 'From' : 'Available From'}
                                            value={values.rental_start}
                                            onChange={newValue =>
                                                setFieldValue('rental_start', newValue)
                                            }
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error:
                                                        touched.rental_start &&
                                                        !!errors.rental_start,
                                                    helperText:
                                                        touched.rental_start &&
                                                        errors.rental_start
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label={isRental ? 'To' : 'Available Until'}
                                            value={values.rental_end}
                                            onChange={newValue =>
                                                setFieldValue('rental_end', newValue)
                                            }
                                            minDate={values?.rental_start}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error:
                                                        touched.rental_end &&
                                                        !!errors.rental_end,
                                                    helperText:
                                                        touched.rental_end &&
                                                        errors.rental_end
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <Divider />
                            <DialogActions sx={{ p: 2.5 }}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <Button
                                            color="error"
                                            variant="text"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>

                                    <Grid item>
                                        <Grid container spacing={2}>
                                            {isRental && (
                                                <Grid item>
                                                    <Button
                                                        color="error"
                                                        variant="outlined"
                                                        onClick={() =>
                                                            setOpenConfirmTerminate(true)
                                                        }
                                                        disabled={loading}
                                                    >
                                                        Terminate Rental
                                                    </Button>
                                                </Grid>
                                            )}
                                            <Grid item>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    onClick={handleSubmit}
                                                    disabled={loading}
                                                >
                                                    {isRental ? 'Update' : 'Rent'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </DialogActions>
                        </LocalizationProvider>
                    )}
                </Formik>
            )}

            <ConfirmModal
                title="Confirm Rent"
                text="Are you sure you want to rent this property?"
                open={openConfirmRent}
                onClose={() => setOpenConfirmRent(false)}
                onConfirm={confirmRent}
                confirmText="Rent"
                type={ConfirmModalType.WARNING}
            />

            <ConfirmModal
                title="Confirm Termination"
                text="Are you sure you want to terminate this rental? The tenant, their guests and invites will be removed."
                open={openConfirmTerminate}
                onClose={() => setOpenConfirmTerminate(false)}
                onConfirm={confirmTerminate}
                confirmText="Terminate"
                type={ConfirmModalType.ERROR}
            />
        </>
    );
};

export default SetUpdateRentalModal;
